import { useState, useEffect, useRef, useCallback } from "react";
import { LazyMotion, domAnimation, m, AnimatePresence } from "framer-motion";
import { LogIn, ArrowRight, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { useLocation } from "react-router-dom";
import AnimatedLogo from "@/components/AnimatedLogo";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const navItems = [
  { label: "Home", path: "/", tooltip: "Back to the homepage" },
  { label: "Services", path: "/services", tooltip: "Explore our platform capabilities" },
  { label: "How It Works", path: "/how-it-works", tooltip: "See our 3-phase launch process" },
  { label: "Growth Plans", path: "/pricing", tooltip: "Explore investment tiers & ROI" },
  { label: "Portfolio", path: "/portfolio", tooltip: "Browse client success stories" },
  { label: "About", path: "/about", tooltip: "Learn about Sullivan Care" },
  { label: "Resources", path: "/resources", tooltip: "Pharmacy growth & technology guides" },
  { label: "FAQ", path: "/faq", tooltip: "Frequently asked questions" },
  { label: "Contact", path: "/contact", tooltip: "Get in touch with our team" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileVisible, setMobileVisible] = useState(true);
  const lastScrollY = useRef(0);
  const location = useLocation();
  const isMobile = useIsMobile();
  const { theme, setTheme } = useTheme();

  const rafRef = useRef<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const wasScrolled = lastScrollY.current > 40;
        const isScrolled = currentY > 40;

        if (wasScrolled !== isScrolled) setScrolled(isScrolled);

        if (isMobile) {
          if (currentY < lastScrollY.current || currentY < 40) {
            setMobileVisible(true);
          } else if (currentY > lastScrollY.current && currentY > 80) {
            setMobileVisible(false);
            setMobileOpen(false);
          }
        }
        lastScrollY.current = currentY;
        rafRef.current = 0;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isMobile]);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    }
  }, [location.pathname]);

  const handleNavClick = (path: string) => {
    setMobileOpen(false);
    if (path.includes("#")) {
      const hash = path.split("#")[1];
      if (location.pathname === "/") {
        setTimeout(() => {
          const el = document.getElementById(hash);
          el?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  };

  return (
    <LazyMotion features={domAnimation}>
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-out ${
        scrolled ? "bg-background/95 backdrop-blur-md shadow-lg" : "bg-transparent"
      } ${isMobile && !mobileVisible ? "-translate-y-full" : "translate-y-0"}`}
      style={{ willChange: "transform" }}
    >
      <div className="container mx-auto flex items-center justify-between pt-1 lg:pt-2 px-1 lg:px-6">
        {/* Logo — small on mobile, large on desktop */}
        <Tooltip>
          <TooltipTrigger asChild>
            <a href="/" className="overflow-hidden flex items-center -ml-3 lg:ml-0">
              <AnimatedLogo className="h-[80px] lg:h-48 -mt-2 -mb-3 lg:-mt-6 lg:-mb-8" />
            </a>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs font-medium">
            Sullivan Care — Healthcare Infrastructure
          </TooltipContent>
        </Tooltip>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1.5 mt-3">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path === "/" && location.pathname === "/");
            return (
              <Tooltip key={item.label}>
                <TooltipTrigger asChild>
                  <a
                    href={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={`px-3 py-1.5 text-sm font-bold rounded-md hover:scale-105 transition-transform duration-200 whitespace-nowrap ${
                      isActive && !item.path.includes("#")
                        ? "bg-lime text-[hsl(var(--navy))] shadow-md shadow-[hsl(var(--lime)/0.3)]"
                        : "btn-gradient-animated"
                    }`}
                  >
                    {item.label}
                  </a>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs font-medium">
                  {item.tooltip}
                </TooltipContent>
              </Tooltip>
            );
          })}

          {/* Enroll Now with Limited Availability badge on top */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="relative ml-3">
                <a href="/contact" className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[hsl(var(--lime))] via-[hsl(var(--lime-glow))] to-[hsl(var(--lime))] text-[hsl(var(--navy))] font-bold px-3.5 py-1.5 text-sm rounded-full ring-1 ring-destructive ring-offset-1 ring-offset-background shadow-[0_0_14px_hsl(var(--lime)/0.4)] hover:shadow-[0_0_20px_hsl(var(--lime)/0.5)] hover:scale-105 transition-all duration-300 animate-pulse-subtle whitespace-nowrap">
                  Enroll Now
                </a>
                <span className="absolute -top-3 -right-3 bg-[hsl(0_80%_55%)] text-white text-[8px] font-bold px-2 py-0.5 rounded-full btn-flash-alert whitespace-nowrap leading-tight z-40 pointer-events-none shadow-lg">
                  Limited Availability
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs font-medium">
              Secure your spot — limited openings this quarter
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button asChild variant="ghost" className="bg-gradient-to-r from-[hsl(var(--lime))] via-[hsl(var(--lime-glow))] to-[hsl(var(--lime))] text-[hsl(var(--navy))] font-extrabold px-5 py-2.5 text-base ml-2 rounded-full shadow-[0_0_16px_hsl(var(--lime)/0.4)] hover:shadow-[0_0_24px_hsl(var(--lime)/0.6)] hover:scale-105 transition-all duration-300 border-0">
                <a href="/signin">
                  <LogIn className="mr-1.5 h-4 w-4" /> Sign In
                </a>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs font-medium">
              Access your provider dashboard
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="ml-2 p-2.5 rounded-md border border-[hsl(var(--lime))]/20 text-foreground hover:bg-[hsl(var(--lime))]/10 transition-colors"
                aria-label="Toggle dark mode"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs font-medium">
              {theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            </TooltipContent>
          </Tooltip>
        </nav>

        {/* Mobile: Enroll button + hamburger */}
        <div className="lg:hidden flex items-center gap-2">
          <div className="relative">
            <a href="/contact" className="inline-flex items-center gap-1 bg-gradient-to-r from-[hsl(var(--lime))] via-[hsl(var(--lime-glow))] to-[hsl(var(--lime))] text-[hsl(var(--navy))] font-semibold px-2.5 py-1 text-[10px] rounded-full ring-1 ring-destructive ring-offset-1 ring-offset-background shadow-[0_0_8px_hsl(var(--lime)/0.25)] animate-pulse-subtle">
              Enroll Now
            </a>
            <span className="absolute -top-1.5 -right-1.5 bg-[hsl(0_80%_55%)] text-white text-[7px] font-bold px-1.5 py-px rounded-full btn-flash-alert whitespace-nowrap leading-tight z-40 pointer-events-none shadow-sm">
              Limited Access
            </span>
          </div>
          <button
            className="relative flex items-center justify-center w-10 h-10 rounded-lg border border-lime/30 bg-navy/80 !p-0"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <div className="flex flex-col items-center justify-center gap-[5px]">
              <span
                className={`block h-[2px] w-5 rounded-full bg-lime transition-all duration-300 ${
                  mobileOpen ? "rotate-45 translate-y-[7px]" : ""
                }`}
              />
              <span
                className={`block h-[2px] w-5 rounded-full bg-lime transition-all duration-300 ${
                  mobileOpen ? "opacity-0 scale-0" : ""
                }`}
              />
              <span
                className={`block h-[2px] w-5 rounded-full bg-lime transition-all duration-300 ${
                  mobileOpen ? "-rotate-45 -translate-y-[7px]" : ""
                }`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-background/95 backdrop-blur-md border-t border-[hsl(var(--lime))]/10 px-6 pb-6 space-y-2 pt-4 overflow-hidden"
          >
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <a
                  key={item.label}
                  href={item.path}
                  onClick={() => handleNavClick(item.path)}
                  className={`block w-full px-5 py-2.5 text-sm font-semibold rounded-lg ${
                    isActive && !item.path.includes("#")
                      ? "bg-lime text-[hsl(var(--navy))] shadow-lg shadow-[hsl(var(--lime)/0.3)]"
                      : "btn-gradient-animated"
                  }`}
                >
                  {item.label}
                </a>
              );
            })}
            <Button asChild variant="ghost" className="w-full bg-gradient-to-r from-[hsl(var(--lime))] via-[hsl(var(--lime-glow))] to-[hsl(var(--lime))] text-[hsl(var(--navy))] font-extrabold py-4 text-sm rounded-full mt-1 shadow-[0_0_16px_hsl(var(--lime)/0.4)] border-0">
              <a href="/signin">
                <LogIn className="mr-2 h-4 w-4" /> Sign In
              </a>
            </Button>
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-foreground rounded-full border border-[hsl(var(--lime))]/20 hover:bg-[hsl(var(--lime))]/10 transition-colors mt-1"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {theme === "dark" ? "Light Mode" : "Dark Mode"}
            </button>
          </m.div>
        )}
      </AnimatePresence>
    </header>
    </LazyMotion>
  );
};

export default Navbar;
